import { memo } from 'react';
import { Calendar, Search, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/src/components/ui';

const features = [
  {
    id: 1,
    title: 'Automatización de Reservas',
    subtitle: 'Airbnb & Booking',
    icon: <Calendar className="text-white" size={20} />,
    visual: (
      <div className="space-y-3 font-mono text-xs p-5 flex flex-col justify-center h-full">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center font-bold text-white text-[10px]">
            USR
          </div>
          <div className="bg-[#1a1a1a] border border-white/10 p-3 rounded-2xl rounded-tl-none text-gray-300 shadow-sm">
            ¿Está libre el apartamento este fin de semana?
          </div>
        </div>
        <div className="flex gap-3 flex-row-reverse">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex-shrink-0 flex items-center justify-center font-bold text-white text-[10px]">
            AI
          </div>
          <div className="bg-indigo-900/20 border border-indigo-500/30 text-indigo-100 p-3 rounded-2xl rounded-tr-none shadow-sm relative overflow-hidden">
            <div className="relative z-10">
              Sí, disponible Vie-Dom ($240 total).
              <div className="mt-2 flex gap-2">
                <button className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-md text-[10px] font-semibold transition-colors">
                  Reservar Ahora
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: 'Búsqueda Semántica',
    subtitle: 'Enterprise Data',
    icon: <Search className="text-white" size={20} />,
    visual: (
      <div className="p-5 flex flex-col h-full relative">
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search size={14} className="text-indigo-400" />
          </div>
          <div className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg py-2.5 pl-9 pr-3 text-xs text-gray-300 font-mono shadow-[inset_0_2px_4px_rgba(0,0,0,0.5)]">
            "churn analysis Q3 sales"
          </div>
          <div className="absolute inset-y-0 right-3 flex items-center">
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(99,102,241,0.8)]" />
          </div>
        </div>

        <div className="space-y-2 flex-1 relative">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 relative group/item hover:bg-white/10 transition-colors cursor-pointer hover:border-indigo-500/30">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="text-[9px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded border border-red-500/30 font-bold">
                PDF
              </div>
              <span className="text-xs text-gray-200 font-medium truncate">
                Q3_Financial_Report_Final.pdf
              </span>
            </div>
            <p className="text-[10px] text-gray-500 leading-relaxed">
              ...observed a significant{' '}
              <span className="text-indigo-200 bg-indigo-500/20 font-semibold px-1 rounded border border-indigo-500/10">
                churn rate increase of 12%
              </span>{' '}
              in the enterprise segment due to...
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-3 opacity-40 grayscale blur-[0.5px]">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[9px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded border border-blue-500/30 font-bold">
                DOCX
              </div>
              <span className="text-xs text-gray-400 font-medium truncate">
                Meeting_Notes_July.docx
              </span>
            </div>
            <div className="space-y-1.5 mt-2">
              <div className="h-1.5 w-full bg-white/10 rounded" />
              <div className="h-1.5 w-2/3 bg-white/10 rounded" />
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

function Features() {
  return (
    <section className="container mx-auto px-6 py-20 relative z-20">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Large Feature Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          className="lg:col-span-2 bg-[#0a0a0a] rounded-3xl border border-white/10 overflow-hidden relative group min-h-[420px] hover:border-white/20 transition-all duration-500 flex flex-col justify-end shadow-2xl"
        >
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center opacity-30 group-hover:scale-105 transition-transform duration-700 saturate-0 group-hover:saturate-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent" />

          <div className="absolute bottom-0 left-0 p-8 md:p-12 z-10 w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-600/20 backdrop-blur-md border border-blue-500/30 text-blue-300 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase">
                CASO DE ÉXITO
              </div>
              <div className="h-px w-12 bg-white/20" />
            </div>

            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">
              Chatbot para Entrenadores
            </h3>
            <p className="text-gray-300 max-w-lg mb-8 text-lg leading-relaxed">
              Automatizamos la entrega de rutinas y seguimiento de dieta vía WhatsApp. Aumentamos la
              retención de clientes un 45% en 3 meses.
            </p>

            <Button icon={<ArrowRight size={16} />}>Ver caso completo</Button>
          </div>
        </motion.div>

        {/* Smaller Interactive Cards */}
        <div className="flex flex-col gap-8">
          {features.map((feature) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              className="flex-1 bg-[#0a0a0a] rounded-3xl border border-white/10 overflow-hidden hover:border-white/20 transition-all duration-300 flex flex-col group shadow-lg"
            >
              <div className="p-5 border-b border-white/5 flex justify-between items-start bg-white/[0.02] group-hover:bg-white/[0.05] transition-colors">
                <div>
                  <h4 className="text-white font-bold group-hover:text-indigo-300 transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mt-1 font-mono">
                    {feature.subtitle}
                  </p>
                </div>
                <div className="bg-white/5 p-2 rounded-lg border border-white/5 group-hover:border-white/10 transition-colors">
                  {feature.icon}
                </div>
              </div>

              <div className="flex-1 bg-[#050505] relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_20px_rgba(0,0,0,1)] z-10" />
                <div className="relative z-0 h-full">{feature.visual}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default memo(Features);
